import {
  CustomPagination,
  serializeSort as defaultSerializeSort,
  formatDate,
  FormattedDate,
  getIsoDateFromFormattedDate,
  ISODate,
  Main,
  Padding,
  ProcessingEventDirectoryEntryType,
  ProcessingEventsSortKey,
  Stack,
  TotalEntries,
  useDirectory,
  useOutputDataProcessingEventsQuery,
  OutputProcessingEventsFilter,
  outputProcessingEventsFilterKeys,
  OutputProcessingEventsFilterKey,
  FilterTransformer,
  serializeValueDate,
  deserializeValueDate,
  SortSerializer,
  EMPTY_PAGE,
  useQueryParams,
  Spinner,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnSort } from '@tanstack/react-table';
import { Flex } from 'antd';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SectionIndexWithMenu } from '../../../common/Section';
import { useSectionBasePath } from '../../../common/SectionContext';
import { dataSections } from '../../Data';
import { OutputDataHeader } from '../OutputDataHeader';
import { OutputDataDirectoryFilter } from '../OutputDataDirectoryFilter';
import { OutputDataProcessingEventsTable } from './OutputDataProcessingEventsTable';
import { useDaysToSetQueryParamsPeriod } from '../../input-data/utils';

const arrayFields: OutputProcessingEventsFilterKey[] = [
  'processingTypeId',
  'errorLevelId',
  'statusId',
  'valueDate',
  'valueDays',
  'startTime',
  'endTime',
  'outputDataObjectGroupType',
];

const numericFields: OutputProcessingEventsFilterKey[] = [
  'processingTypeId',
  'errorLevelId',
  'statusId',
  'valueDays',
];

const booleanFields: OutputProcessingEventsFilterKey[] = ['onlyLastValueDate'];

export function OutputDataProcessingEventsDirectory() {
  useDaysToSetQueryParamsPeriod();

  const [{ valueDays }] = useQueryParams();
  if (valueDays) {
    return <Spinner />;
  }

  return <OutputDataProcessingEventsDirectoryContent />;
}

export const OutputDataProcessingEventsDirectoryContent: FC = () => {
  const {
    headerProps,
    tableProps,
    sidePanelFilterProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: outputProcessingEventsFilterKeys,
    initialFilters,
    initialSort,
    arrayFields,
    numericFields,
    booleanFields,
    serializeFilter,
    deserializeFilter,
    serializeSort,
  });

  const [refetch, setRefetch] = useState(false);

  const { data = EMPTY_PAGE, isFetching } = useOutputDataProcessingEventsQuery(
    queryParameters,
    { placeholderData: keepPreviousData },
    refetch,
  );

  useEffect(() => {
    setTotal(data?.totalElements);
  }, [setTotal, data?.totalElements]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const sectionBasePath = useSectionBasePath();

  return (
    <SectionIndexWithMenu
      sections={dataSections}
      basePath={sectionBasePath}
      selectedKeys={['output-data']}
    >
      <Main>
        <Main.Col border={false}>
          <Main.Content
            padding={false}
            id="processing-errors-directory-content"
          >
            <DirectoryWrapper size="md">
              <Padding top horizontal>
                <OutputDataHeader
                  {...headerProps}
                  infoIconUrl={
                    InfoIconUrl.OUTPUT_DATA_PROCESSING_EVENTS_DIRECTORY
                  }
                  valueDate={queryParameters.valueDate}
                  onFilterToggle={() => setIsFilterOpen((prev) => !prev)}
                  isFilterOpen={isFilterOpen}
                  onRefresh={() => setRefetch((prev) => !prev)}
                  isLoading={isFetching}
                />
              </Padding>
              <Padding bottom horizontal>
                <Flex vertical gap="small">
                  <OutputDataProcessingEventsTable
                    {...tableProps}
                    data={data?.content ?? EMPTY_DATA}
                    isLoading={isFetching}
                  />
                </Flex>
              </Padding>
            </DirectoryWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={data?.totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
        {isFilterOpen && (
          <OutputDataDirectoryFilter
            onClose={() => setIsFilterOpen(false)}
            initialValues={queryParameters}
            {...sidePanelFilterProps}
          />
        )}
      </Main>
    </SectionIndexWithMenu>
  );
};

const EMPTY_DATA: ProcessingEventDirectoryEntryType[] = [];

const initialFilters: OutputProcessingEventsFilter = {
  createdBy: undefined,
  endTime: undefined,
  errorLevelId: [],
  processingTypeId: [],
  startTime: undefined,
  statusId: [],
  valueDate: undefined,
  valueDays: undefined,
  onlyLastValueDate: undefined,
  dataManager: undefined,
  outputProcessingDataObjectId: undefined,
  outputDataObjectDescriptionId: undefined,
  outputDataObjectDescription: undefined,
  outputDataObjectGroupType: [],
  outputDataObjectDescriptionParent: undefined,
  outputCode: undefined,
  sourceCode: undefined,
};

const initialSort = [{ id: 'endTime', desc: true }];

const dateRangeFields = ['startTime', 'endTime'];

const serializeFilter: FilterTransformer<
  string,
  OutputProcessingEventsFilterKey
> = (...args) => {
  const [[id, value], ...rest] = Object.entries(
    serializeValueDate(args[0], args[1]),
  );
  return {
    [id]: dateRangeFields.includes(id)
      ? (value as FormattedDate[] | undefined)?.map(getIsoDateFromFormattedDate)
      : value,
    ...Object.fromEntries(rest),
  };
};

const deserializeFilter: FilterTransformer<OutputProcessingEventsFilterKey> = (
  id,
  value,
  allQueryFilters,
) => {
  if (dateRangeFields.includes(id)) {
    return {
      [id]: (value as ISODate[] | undefined)?.map((d) => formatDate(d)),
    };
  }

  return deserializeValueDate(id, value, allQueryFilters);
};

const serializeSort: SortSerializer<ProcessingEventsSortKey> = ({
  id,
  desc,
}: ColumnSort) => {
  const ids = [id];

  if (id === 'createdBy') {
    ids.splice(0, 1, 'creatorUsername', 'creatorFirstName', 'creatorLastName');
  }

  return ids.map((id) => defaultSerializeSort({ id, desc }));
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const DirectoryWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
